import styled, { css } from 'styled-components'
import theme from '../../../styles/theme'
import fonts from '../../../styles/fonts'

const sizes = {
  small: css`
    display: flex;
    min-width: 10.5rem;
    max-width: 15rem;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    align-self: stretch;
    padding: 14px 14px 16px 10px;
  `,
  medium: css`
    display: flex;
    min-width: 232px;
    max-width: 16rem;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    align-self: stretch;
    padding: 14px 14px 16px 10px;
  `,
  large: css`
    display: flex;
    min-width: 16.5rem;
    max-width: 18rem;
    height: 48px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  `,
  xlarge: css`
    display: flex;
    min-width: 16.5rem;
    width: 360px;
    height: 48px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  `,
  mobile: css`
    display: flex;
    width: 100%;
    height: 48px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  `,
  text: css`
    display: flex;
    width: 100%;
    height: 48px;
    justify-content: flex-start;
    align-items: center;
    flex-shrink: 0;
  `,
  card: css`
    display: flex;
    width: 100%;
    max-width: 100%;
    padding: 18px 16px;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
  `,
}

const variants = {
  primary: css`
    background-color: ${theme.colors.brand.blueOmint};
    color: ${theme.colors.brand.white};
    &:hover {
      background-color: ${theme.colors.brand.blueDark};
    }
    &:disabled {
      color: ${theme.colors.text.secondaryText};
      background-color: ${theme.colors.greys.dividerGray};
    }
    &:focus {
      border: 3px solid ${theme.colors.brand.white};
    }
    &:active {
      border: none;
    }
  `,
  secondary: css`
    background-color: ${theme.colors.brand.white};
    color: ${theme.colors.brand.blueOmint};
    border: 1px solid ${theme.colors.brand.blueOmint};

    &:hover {
      background-color: ${theme.colors.brand.blueOmint};
      color: ${theme.colors.brand.white};
    }
    &:disabled {
      color: ${theme.colors.text.secondaryText};
      background-color: ${theme.colors.greys.dividerGray};
      border: 1px solid ${theme.colors.text.secondaryText};
    }
    &:focus {
      border: 3px solid ${theme.colors.brand.blueOmint};
    }
    &:active {
      border: none;
    }
  `,
  tertiary: css`
    background-color: ${theme.colors.brand.white};
    color: ${theme.colors.brand.blueOmint};
    text-decoration: ${({ $underline }) =>
      $underline== true ? 'underline' : 'none'};
    align-self: flex-start;
    width: auto;
    margin-top: 15px;
    padding: 0;

    &:hover {
      color: ${theme.colors.lightBlue};
      & > div > svg > path {
        stroke: ${theme.colors.lightBlue};
      }
    }
    &:disabled {
      color: ${theme.colors.text.secondaryText};
      background-color: ${theme.colors.brand.white};
    }
    &:focus {
      ${({ $showBorder = true }) =>
        $showBorder &&
        css`
          border: 2px solid ${theme.colors.brand.blueOmint};
        `}
    }
    &:active {
      border: none;
    }
  `,
  cardNav: css`
    background-color: ${theme.colors.brand.white};
    color: ${theme.colors.text.primaryText};
    border: 1px solid ${theme.colors.greys.dividerGray};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    font-weight: 400;
    font-size: ${fonts.fontSizes.lg};
    &:hover {
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
      border-color: ${theme.colors.greys.mediumGray};
      
      & svg {
        transform: translateX(4px);
      }
    }

    &:disabled {
      color: ${theme.colors.text.secondaryText};
      background-color: ${theme.colors.greys.lightGray};
      border: 1px solid ${theme.colors.greys.dividerGray};
      cursor: not-allowed;
    }

    &:active {
      transform: scale(0.99);
    }

    & svg {
      transition: transform 0.2s ease;
      color: ${theme.colors.brand.blueOmint};
    }
  `,
}

export const StyledButton = styled.button`
  font-family: 'Source Sans Pro', system-ui, Avenir, Helvetica, Arial,
    sans-serif;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  display: inline-block;
  line-height: 1;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: ${fonts.fontSizes.md};
  align-self: ${({ alignSelf }) => alignSelf || 'initial'} !important;
  max-width: ${props => props.maxWidth ? '359px' : 'none'};
  ${({ size }) => size && sizes[size]}
  ${({ $variant }) => $variant && variants[$variant]}

  ${({ backgroundColor }) =>
    backgroundColor &&
    css`
      background-color: ${theme.colors.brand[backgroundColor] || theme.colors[backgroundColor]};
      color: ${backgroundColor === 'white' ? theme.colors.brand.blueOmint : theme.colors.brand.white};
    `}

  ${({ hoverColor }) =>
    hoverColor &&
    css`
      &:hover {
        color: ${hoverColor === 'tertiary' ? theme.colors.brand.blueOmint : theme.colors.brand.white};
        background-color: ${theme.colors.brand[hoverColor] || theme.colors[hoverColor]};
      }
    `}
  @media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
    max-width: ${({ maxWidth }) => maxWidth || '398px'};
  }

  &:disabled {
    cursor: not-allowed;
  }
`